import * as React from "react";
import { AnimatePresence, motion, MotionConfig } from "framer-motion";

import { cn } from "@/lib/utils";
import { ContextMenuItem } from "@/components/ui/context-menu";

const RESET_AFTER_MS = 3000;

/**
 * Two-step confirm state: the first press arms, the second confirms.
 * Disarms on its own after a few seconds so a stray click can't linger.
 */
export function useInlineConfirm(resetAfterMs = RESET_AFTER_MS) {
  const [armed, setArmed] = React.useState(false);

  React.useEffect(() => {
    if (!armed) return;
    const timer = window.setTimeout(() => setArmed(false), resetAfterMs);
    return () => window.clearTimeout(timer);
  }, [armed, resetAfterMs]);

  const arm = React.useCallback(() => setArmed(true), []);
  const disarm = React.useCallback(() => setArmed(false), []);
  return { armed, arm, disarm };
}

interface InlineConfirmLabelProps {
  armed: boolean;
  idle: React.ReactNode;
  confirm: React.ReactNode;
}

/** Crossfades between the idle and armed labels. */
function InlineConfirmLabel({ armed, idle, confirm }: InlineConfirmLabelProps) {
  return (
    <MotionConfig reducedMotion="user">
      <span className="relative inline-flex min-w-0 overflow-hidden">
        <AnimatePresence mode="wait" initial={false}>
          <motion.span
            key={armed ? "confirm" : "idle"}
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.12 }}
            className="leading-snug"
          >
            {armed ? confirm : idle}
          </motion.span>
        </AnimatePresence>
      </span>
    </MotionConfig>
  );
}

interface InlineConfirmMenuItemProps {
  icon?: React.ReactNode;
  label: React.ReactNode;
  confirmLabel: React.ReactNode;
  onConfirm: () => void;
  /** Called when the item arms, e.g. to load a count for the confirm label. */
  onArm?: () => void;
  className?: string;
}

/**
 * Context menu item that turns into its own confirm. The menu stays open
 * after the first select; closing the menu unmounts it, which disarms it.
 */
export function InlineConfirmMenuItem({ icon, label, confirmLabel, onConfirm, onArm, className }: InlineConfirmMenuItemProps) {
  const { armed, arm } = useInlineConfirm();

  return (
    <ContextMenuItem
      onSelect={(event) => {
        if (!armed) {
          event.preventDefault();
          arm();
          onArm?.();
          return;
        }
        onConfirm();
      }}
      data-armed={armed || undefined}
      className={cn(
        "text-destructive focus:bg-destructive focus:text-white",
        armed && "bg-destructive text-white font-medium focus:bg-destructive",
        className,
      )}
    >
      {icon}
      {/* div, not span: ContextMenuItem dims direct span children (shortcut hints) on focus */}
      <div className="min-w-0">
        <InlineConfirmLabel armed={armed} idle={label} confirm={confirmLabel} />
      </div>
    </ContextMenuItem>
  );
}

interface InlineConfirmButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "onClick"> {
  idleContent: React.ReactNode;
  confirmContent: React.ReactNode;
  onConfirm: () => void;
  armedClassName?: string;
}

/** Button version for places outside a menu, such as a list row action. */
export function InlineConfirmButton({
  idleContent,
  confirmContent,
  onConfirm,
  className,
  armedClassName,
  ...props
}: InlineConfirmButtonProps) {
  const { armed, arm, disarm } = useInlineConfirm();

  return (
    <button
      type="button"
      {...props}
      onClick={() => {
        if (!armed) {
          arm();
          return;
        }
        disarm();
        onConfirm();
      }}
      data-armed={armed || undefined}
      className={cn(className, armed && armedClassName)}
    >
      <InlineConfirmLabel armed={armed} idle={idleContent} confirm={confirmContent} />
    </button>
  );
}
